import { useCallback, useState } from "react";
import { customerOrdersService } from "~/connectors/orders.connector";
import { LLMChatMessage, Order, OrderFormDataDetails } from "~/types";


type AuthStatus = "loading" | "authenticated" | "unauthenticated";

const MIN_STORY_COMPLETION_PERCENTAGE = 20;
const MIN_STORY_LENGTH = 20;

export default function useStoryGeneration({
  authStatus,
  anonymousId,
  formData,
  setFormData,
  setOrder,
}: {
  authStatus: AuthStatus;
  anonymousId: string | null;
  formData: OrderFormDataDetails;
  setFormData: React.Dispatch<React.SetStateAction<OrderFormDataDetails>>;
  setOrder: (o: Order | null) => void;
}) {
  const [validatingStory, setValidatingStory] = useState(false);

  const addStoryMessage = useCallback(
    async (message: string, shouldReplaceLastUserMessage = false) => {
      const trimmed = message.trim();

      // Special chat command: /new — reset story-related fields but keep name/age/gender
      if (trimmed.toLowerCase() === "/new") {
        setValidatingStory(true);

        // capture minimal form data from previous state
        let minimalFormData: Partial<OrderFormDataDetails> = {
          childName: formData.childName,
          age: formData.age,
          gender: formData.gender,
        };
        setFormData(minimalFormData);

        try {
          if (authStatus === "authenticated") {
            const updatedOrder = await customerOrdersService.upsertDraft({
              formData: minimalFormData,
            });
            setOrder(updatedOrder);
            setFormData(updatedOrder.formData || {});
          } else if (authStatus === "unauthenticated" && anonymousId) {
            const updatedDraft = await customerOrdersService.upsertAnonymousDraft(
              anonymousId,
              minimalFormData,
            );
            setFormData(updatedDraft.data);
          }
        } catch (err) {
          console.error("Error resetting story draft:", err);
        } finally {
          setValidatingStory(false);
        }

        return false;
      }

      setValidatingStory(true);

      // optimistically add the user message to the thread so that it shows up immediately in the UI
      const userMsg: LLMChatMessage = { role: "user", content: message };
      setFormData((prev) => {
        const baseThread = shouldReplaceLastUserMessage
          ? (prev.storyThread || []).slice(0, -2)
          : prev.storyThread || [];
        return { ...prev, storyThread: [...baseThread, userMsg] };
      });

      try {
        if (authStatus === "authenticated") {
          const updatedOrder = await customerOrdersService.chatDraft(
            message,
            shouldReplaceLastUserMessage,
          );
          setOrder(updatedOrder);
          setFormData(updatedOrder.formData || {});
          setValidatingStory(false);
          return (
            (updatedOrder.formData?.estimatedCompletionPercentage ?? 0) >
            MIN_STORY_COMPLETION_PERCENTAGE
          );
        } else {
          const updatedDraft = await customerOrdersService.chatAnonymousDraft(
            anonymousId!,
            message,
            shouldReplaceLastUserMessage,
          );
          setFormData(updatedDraft.data);
          setValidatingStory(false);
          return (
            (updatedDraft.data?.estimatedCompletionPercentage ?? 0) >
            MIN_STORY_COMPLETION_PERCENTAGE
          );
        }
      } catch (error) {
        console.error("Error sending story message:", error);
        // if we have an error show it to the user and let them try again
        // add a message from model with the error isError
        const errorMessage: LLMChatMessage = {
          role: "model",
          content:
            "Se pare că muza noastră a luat o scurtă pauză. Apasă butonul și încercăm din nou!",
          isError: true,
        };
        setFormData((prev) => ({
          ...prev,
          storyThread: [...(prev.storyThread || []), errorMessage],
        }));

        setValidatingStory(false);
        return false;
      }
    },
    [authStatus, anonymousId, formData, setFormData, setOrder],
  );

  return { validatingStory, addStoryMessage };
}
